import React, { useState } from 'react';
import { Users, Music, Lightbulb, Star, Send, Check } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { branding } from '../config/branding';
import { Button } from '../components/ui/button';
import { toast } from '../hooks/use-toast';
import { Checkbox } from '../components/ui/checkbox';

const Collaborate = () => {
  const { currentTheme } = useTheme();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    artistName: '',
    genre: 'Hip-Hop',
    experience: 'intermediate',
    projectDescription: '',
    previousWork: '',
    optInNewsletter: false,
    optInUpdates: false
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    toast({
      title: "Collaboration Request Sent!",
      description: "Thanks for your interest! We'll review your submission and get back to you within 3-5 business days.",
    });
    setFormData({
      name: '',
      email: '',
      artistName: '',
      genre: 'Hip-Hop',
      experience: 'intermediate',
      projectDescription: '',
      previousWork: '',
      optInNewsletter: false,
      optInUpdates: false
    });
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleCheckboxChange = (field, checked) => {
    setFormData(prev => ({
      ...prev,
      [field]: checked
    }));
  };

  return (
    <div className="min-h-screen bg-black pt-24 pb-32">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 animate-pulse"
               style={{ background: `linear-gradient(135deg, ${currentTheme.primary}, ${currentTheme.accent || currentTheme.primary}dd)` }}>
            <Users className="w-10 h-10 text-black" />
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">Let's Collaborate</h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Join forces with {branding.siteName} to create something extraordinary. Whether you're an artist, producer, or creative visionary, let's build together.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          {/* Benefits Section */}
          <div className="space-y-6">
            <h2 className="text-3xl font-bold text-white mb-6">Why Collaborate?</h2>
            
            {[
              {
                icon: Star,
                title: 'Professional Quality',
                description: '20 years of production experience delivering industry-standard results'
              },
              {
                icon: Music,
                title: 'Custom Production',
                description: 'Tailored beats that match your unique style and artistic vision'
              },
              {
                icon: Lightbulb,
                title: 'Creative Partnership',
                description: 'Work directly with Tha Advocate Beats to bring your ideas to life'
              },
              {
                icon: Users,
                title: 'Artist Network',
                description: 'Join a growing community of 50+ artists and counting'
              }
            ].map((benefit, idx) => (
              <div 
                key={idx}
                className="flex gap-4 p-6 rounded-2xl bg-zinc-900/50 backdrop-blur-sm border border-white/10 hover:bg-zinc-900 transition-all duration-300 group"
              >
                <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform"
                     style={{ backgroundColor: `${currentTheme.primary}20` }}>
                  <benefit.icon className="w-6 h-6" style={{ color: currentTheme.primary }} />
                </div>
                <div>
                  <h3 className="text-white font-bold text-lg mb-2">{benefit.title}</h3>
                  <p className="text-gray-400">{benefit.description}</p>
                </div>
              </div>
            ))}

            {/* Past Collaborations */}
            <div className="mt-8 p-6 rounded-2xl bg-zinc-900/50 backdrop-blur-sm border border-white/10">
              <h3 className="text-white font-bold text-xl mb-4">What Artists Say</h3>
              <div className="space-y-4">
                <div className="border-l-2 pl-4 border-white/20">
                  <p className="text-gray-300 italic">"Working with Tha Advocate Beats took my music to the next level. The attention to detail is unmatched."</p>
                  <p className="text-gray-500 text-sm mt-2">- DJ Smooth, Atlanta</p>
                </div>
                <div className="border-l-2 pl-4 border-white/20">
                  <p className="text-gray-300 italic">"Professional, creative, and always delivers on time. Best producer I've worked with."</p>
                  <p className="text-gray-500 text-sm mt-2">- MC Flow, NYC</p>
                </div>
              </div>
            </div>
          </div>

          {/* Collaboration Request Form */}
          <div className="bg-zinc-900 rounded-2xl p-8 border border-white/10">
            <h2 className="text-2xl font-bold text-white mb-6">Submit Your Collaboration Request</h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-gray-400 text-sm mb-2 block">Your Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg bg-black/50 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:ring-2 transition-all"
                    style={{ '--tw-ring-color': currentTheme.primary }}
                    placeholder="John Doe"
                  />
                </div>

                <div>
                  <label className="text-gray-400 text-sm mb-2 block">Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg bg-black/50 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:ring-2 transition-all"
                    style={{ '--tw-ring-color': currentTheme.primary }}
                    placeholder="john@example.com"
                  />
                </div>
              </div>

              <div>
                <label className="text-gray-400 text-sm mb-2 block">Artist/Stage Name *</label>
                <input
                  type="text"
                  name="artistName"
                  value={formData.artistName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg bg-black/50 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:ring-2 transition-all"
                  style={{ '--tw-ring-color': currentTheme.primary }}
                  placeholder="Your artist name"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-gray-400 text-sm mb-2 block">Primary Genre *</label>
                  <select
                    name="genre"
                    value={formData.genre}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg bg-black/50 border border-white/10 text-white focus:outline-none focus:ring-2 transition-all"
                    style={{ '--tw-ring-color': currentTheme.primary }}
                  >
                    <option value="Hip-Hop">Hip-Hop</option>
                    <option value="Trap">Trap</option>
                    <option value="Boom Bap">Boom Bap</option>
                    <option value="Drill">Drill</option>
                    <option value="Lo-Fi">Lo-Fi</option>
                    <option value="R&B">R&B</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="text-gray-400 text-sm mb-2 block">Experience Level *</label>
                  <select
                    name="experience"
                    value={formData.experience}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg bg-black/50 border border-white/10 text-white focus:outline-none focus:ring-2 transition-all"
                    style={{ '--tw-ring-color': currentTheme.primary }}
                  >
                    <option value="beginner">Beginner (0-2 years)</option>
                    <option value="intermediate">Intermediate (2-5 years)</option>
                    <option value="advanced">Advanced (5+ years)</option>
                    <option value="professional">Professional</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="text-gray-400 text-sm mb-2 block">Project Description *</label>
                <textarea
                  name="projectDescription"
                  value={formData.projectDescription}
                  onChange={handleChange}
                  required
                  rows={4}
                  className="w-full px-4 py-3 rounded-lg bg-black/50 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:ring-2 transition-all resize-none"
                  style={{ '--tw-ring-color': currentTheme.primary }}
                  placeholder="Tell us about your project, goals, and what you're looking for in a collaboration..."
                />
              </div>

              <div>
                <label className="text-gray-400 text-sm mb-2 block">Previous Work / Links (optional)</label>
                <input
                  type="text"
                  name="previousWork"
                  value={formData.previousWork}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg bg-black/50 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:ring-2 transition-all"
                  style={{ '--tw-ring-color': currentTheme.primary }}
                  placeholder="SoundCloud, Spotify, YouTube links, etc."
                />
              </div>

              {/* Opt-in Checkboxes */}
              <div className="space-y-3 p-4 rounded-lg bg-black/30 border border-white/5">
                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="newsletter"
                    checked={formData.optInNewsletter}
                    onCheckedChange={(checked) => handleCheckboxChange('optInNewsletter', checked)}
                    className="mt-1"
                  />
                  <label htmlFor="newsletter" className="text-sm text-gray-400 cursor-pointer">
                    <Check className="w-4 h-4 inline mr-1" style={{ color: currentTheme.primary }} />
                    Yes, I want to receive the {branding.siteName} newsletter with exclusive beats, production tips, and special offers
                  </label>
                </div>

                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="updates"
                    checked={formData.optInUpdates}
                    onCheckedChange={(checked) => handleCheckboxChange('optInUpdates', checked)}
                    className="mt-1"
                  />
                  <label htmlFor="updates" className="text-sm text-gray-400 cursor-pointer">
                    <Check className="w-4 h-4 inline mr-1" style={{ color: currentTheme.primary }} />
                    Keep me updated on new releases and collaboration opportunities
                  </label>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-12 text-black font-bold text-lg hover:scale-105 transition-transform"
                style={{ backgroundColor: currentTheme.primary }}
              >
                <Send className="w-5 h-5 mr-2" />
                Submit Collaboration Request
              </Button>

              <p className="text-gray-500 text-xs text-center">
                We review all submissions carefully and respond within 3-5 business days
              </p>
            </form>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-white mb-8 text-center">Collaboration FAQ</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              {
                q: "How long does the collaboration process take?",
                a: "Typically 2-4 weeks depending on project complexity and revisions needed."
              },
              {
                q: "What's included in a collaboration?",
                a: "Custom beat production, unlimited revisions, exclusive rights options, and mixing/mastering."
              },
              {
                q: "Do I need experience to collaborate?",
                a: "Not at all! We work with artists at all levels, from beginners to professionals."
              },
              {
                q: "What are the costs?",
                a: "We'll discuss pricing based on your specific project needs during our initial consultation."
              }
            ].map((faq, idx) => (
              <div key={idx} className="p-6 rounded-xl bg-zinc-900/50 border border-white/10">
                <h3 className="text-white font-bold mb-2">{faq.q}</h3>
                <p className="text-gray-400 text-sm">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Collaborate;